import React, { useCallback, useMemo } from 'react'
import { config } from '../../config'
import { useAuthActions, useAuthValue } from '../../contexts/AuthContext'
import { useAuthConfig } from '../../contexts/AuthConfigContext'
import { useSessionInactivityLogout } from '../../hooks/useSessionInactivityLogout'
import { logger } from '../../utils/logger'

/**
 * Wires idle-timeout logout when the user is authenticated.
 * Timeout is resolved from backend /auth/config, else VITE_SESSION_INACTIVITY_TIMEOUT_MINUTES (default 1440).
 * Set minutes to 0 on either side to disable.
 */
export const SessionInactivityMonitor: React.FC = () => {
  const { isAuthenticated } = useAuthValue()
  const { endSession } = useAuthActions()
  const { config: serverAuthConfig } = useAuthConfig()

  const timeoutMs = useMemo(() => {
    const serverMinutes = serverAuthConfig?.session_inactivity_timeout_minutes
    const minutes =
      serverMinutes !== undefined && serverMinutes !== null
        ? serverMinutes
        : config.auth.sessionInactivityTimeoutMinutes
    if (minutes <= 0) return 0
    return minutes * 60 * 1000
  }, [serverAuthConfig?.session_inactivity_timeout_minutes])

  const handleInactivityTimeout = useCallback(async () => {
    try {
      await endSession('inactivity')
    } catch (err) {
      logger.error(
        'Logout after inactivity failed',
        err instanceof Error ? err : new Error(String(err))
      )
    }
  }, [endSession])

  useSessionInactivityLogout({
    enabled: isAuthenticated && timeoutMs > 0,
    timeoutMs,
    onTimeout: handleInactivityTimeout,
  })

  return null
}
